import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { GitBranch, Plus } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { offerVersionEndpoints } from '@ooh/contracts';
import type { Offer, OfferVersion } from '@ooh/contracts';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { SectionSkeleton } from '../../layouts/skeletons';
import { offerVersionsRequest } from './api/queries';
import { PublishVersionDialog } from './publish-version-dialog';
import { WithdrawAcceptanceDialog } from './withdraw-acceptance-dialog';
import { VersionRowList } from './version-row';
import { VersionDiffDialog } from './version-diff-dialog';
import type { WithdrawAcceptanceMode } from './version-row-actions';

// ─── Diff dialog target lookup ─────────────────────────────────
// Extracted so VersionHistoryTab's own render stays a flat branch (no nested
// ternary) — see the withdrawTarget comment below for the same rationale.

function findDiffVersions(
  sortedNewestFirst: OfferVersion[],
  diffVersionId: number | null,
): { current: OfferVersion; prior: OfferVersion } | null {
  const index = sortedNewestFirst.findIndex((version) => version.id === diffVersionId);
  const current = sortedNewestFirst[index];
  const prior = sortedNewestFirst[index + 1];
  if (!current || !prior) return null;
  return { current, prior };
}

// ─── Empty state ──────────────────────────────────────────────

function VersionsEmptyState({
  canEdit,
  onPublishClick,
}: {
  canEdit: boolean;
  onPublishClick: () => void;
}) {
  const { t } = useTranslation('offers');

  return (
    <div
      className="flex flex-col items-center justify-center h-full py-24 px-8 text-center"
      data-testid="versions-empty-state"
    >
      <div className="w-16 h-16 rounded-full bg-muted/60 flex items-center justify-center mb-6">
        <GitBranch className="h-8 w-8 text-muted-foreground/60" strokeWidth={1.5} />
      </div>
      <h3 className="text-sm font-semibold mb-2">{t('workspace.versions.emptyState.title')}</h3>
      <p className="text-sm text-muted-foreground mb-8 max-w-sm">
        {t('workspace.versions.emptyState.description')}
      </p>
      {canEdit ? (
        <Button data-testid="publish-version-btn" onClick={onPublishClick}>
          <Plus className="h-4 w-4" />
          {t('workspace.versions.emptyState.publishFirstBtn')}
        </Button>
      ) : null}
    </div>
  );
}

// ─── Toolbar ─────────────────────────────────────────────────

// Publish gated on canEdit (false for a read-only share holder and for a
// CANCELLED offer, per workspace-access.ts's isOfferReadOnly) — a cancelled
// campaign has no publish path and SHALL NOT offer one
// (offer-lifecycle-reversibility / "Edit-Gating UX on Non-DRAFT Offers").
function VersionsToolbar({
  canEdit,
  onPublishClick,
}: {
  canEdit: boolean;
  onPublishClick: () => void;
}) {
  const { t } = useTranslation('offers');

  return (
    <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-muted/20 shrink-0">
      <div className="flex items-center gap-2">
        <GitBranch className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{t('workspace.versions.toolbar.title')}</span>
      </div>
      {canEdit ? (
        <Button data-testid="publish-version-btn" onClick={onPublishClick}>
          <Plus className="h-4 w-4" />
          {t('workspace.versions.toolbar.publishBtn')}
        </Button>
      ) : null}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────

export interface VersionHistoryTabProps {
  offerId: string;
  offer: Pick<Offer, 'updatedAt'>;
  canEdit: boolean;
  onPublishClick?: () => void;
  /** Opens the "Udostępnij klientowi" dialog for the active version's card — CU-86caypxpc. */
  onOpenClientShare?: () => void;
  onOpenVersion: (versionId: number) => void;
}

export function VersionHistoryTab(props: VersionHistoryTabProps) {
  return (
    <QueryBoundary
      fallback={
        <div className="p-6">
          <SectionSkeleton />
        </div>
      }
    >
      <VersionHistoryContent {...props} />
    </QueryBoundary>
  );
}

function VersionHistoryContent({
  offerId,
  offer,
  canEdit,
  onPublishClick,
  onOpenClientShare,
  onOpenVersion,
}: VersionHistoryTabProps) {
  const [publishDialogOpen, setPublishDialogOpen] = React.useState(false);
  const [diffVersionId, setDiffVersionId] = React.useState<number | null>(null);
  // Defaulted (never null) rather than `T | null` so the dialog's props below
  // need no optional chaining / nullish coalescing — keeps this component's
  // cyclomatic complexity within the lint budget.
  const [withdrawTarget, setWithdrawTarget] = React.useState<{
    versionId: number | null;
    mode: WithdrawAcceptanceMode;
  }>({ versionId: null, mode: 'withdraw' });

  const { data } = useApiSuspenseQuery(offerVersionEndpoints.list, offerVersionsRequest(offerId));

  function handlePublishClick() {
    setPublishDialogOpen(true);
    onPublishClick?.();
  }

  const versions = data.versions;
  // API returns ascending; reverse for newest-first display
  const sortedNewestFirst = [...versions].reverse();
  const isEmpty = versions.length === 0;
  const diffVersions = findDiffVersions(sortedNewestFirst, diffVersionId);

  return (
    <>
      <div>
        {/* Toolbar — only shown when versions exist. Sticky, pinned below the
            workspace tabs bar (top-11 == its h-11 in workspace-shell.tsx) so
            scrolling over the content below scrolls the page instead of a
            nested scroll box. */}
        {!isEmpty ? (
          <div className="sticky top-11 z-10 bg-background">
            <VersionsToolbar canEdit={canEdit} onPublishClick={handlePublishClick} />
          </div>
        ) : null}

        {/* Content */}
        {isEmpty ? (
          <VersionsEmptyState canEdit={canEdit} onPublishClick={handlePublishClick} />
        ) : (
          <VersionRowList
            sortedNewestFirst={sortedNewestFirst}
            offerId={offerId}
            onCompareClick={setDiffVersionId}
            canEdit={canEdit}
            onWithdrawClick={(versionId, mode) => setWithdrawTarget({ versionId, mode })}
            onOpenClientShare={onOpenClientShare}
            onOpenVersion={onOpenVersion}
          />
        )}
      </div>

      <PublishVersionDialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
        offerId={offerId}
        offer={offer}
      />

      <WithdrawAcceptanceDialog
        open={withdrawTarget.versionId != null}
        onClose={() => setWithdrawTarget({ versionId: null, mode: 'withdraw' })}
        offerId={offerId}
        versionId={withdrawTarget.versionId}
        mode={withdrawTarget.mode}
      />

      {diffVersions ? (
        <VersionDiffDialog
          offerId={offerId}
          currentVersion={diffVersions.current}
          priorVersion={diffVersions.prior}
          onClose={() => setDiffVersionId(null)}
        />
      ) : null}
    </>
  );
}
